import {
	Button,
	Card,
	CardContent,
	FormControlLabel,
	Grid2,
	Paper,
	Switch,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	GridReadyEvent,
	MenuItemDef,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { Log } from "../../models/Log";
import { TagFilterType } from "../../models/Tag";
import { TagFilter } from "../Filters/TagFilter";
import { CodeFilter } from "../Filters/codeFilter";
import { DateFilter } from "../Filters/dateFilter";
import { SearchFilter } from "../Filters/searchFilter";
import { useLogTableData } from "./customHooks/useLogTableData";

import "ag-grid-enterprise";
import { get, isNil } from "lodash-es";
import moment from "moment";
import { CenteredLazyLoading } from "../../../../../components/shared/CenteredLazyLoading";
import { useContextMenuMESF } from "../../../../../controls/contextMenu";
import { themeDXT } from "../../../../../css/themeMESF";
import { LOG_TYPE_CODES } from "../../data/LogTypeCodes";
import { getLogs } from "../../repositories/LogsRepository";
import { TimezoneSelector } from "../Filters/TimezoneSelector";
import { ModalLogSelected } from "../ModalLogSelected/ModalLogSelected";
import { useLogsOptionsFunctions } from "./customHooks/useLogsOptionsFunctions";

type UseLogsProps = {
	startDate: Date;
	endDate: Date | null;
	logTypeCode: number | string | null;
	autoRefresh: boolean;
	tagFilters: TagFilterType[];
};

const useSearchLogs = ({
	startDate,
	endDate,
	logTypeCode,
	autoRefresh,
	tagFilters,
}: UseLogsProps) => {
	return useQuery<Log[], Error>({
		queryKey: [
			"logs",
			startDate.getTime(),
			endDate?.getTime(),
			logTypeCode,
			JSON.stringify(tagFilters),
		],
		queryFn: ({ signal }) =>
			getLogs(
				{
					startDate,
					endDate,
					logTypeCode,
					tagFilters,
				},
				signal,
			),
		refetchInterval: autoRefresh ? 5_000 : false,
		enabled: !!startDate && (!!endDate || autoRefresh),
		keepPreviousData: true,
		staleTime: 10_000,
	});
};

const getLogTypeByCodeId = (logTypeCodeId: string) => {
	return get(LOG_TYPE_CODES, `${logTypeCodeId}.description`, " -");
};

type LogFilters = {
	startDate: Date;
	endDate: Date | null;
	logTypeCode: string[];
	autoRefresh: boolean;
	tagFilters: TagFilterType[];
};

const getDefaultFilters = (): LogFilters => ({
	startDate: moment().add(-5, "days").hour(0).minute(0).second(0).toDate(),
	endDate: moment().hour(23).minute(59).second(59).toDate(),
	logTypeCode: ["I", "W", "E"],
	autoRefresh: false,
	tagFilters: [],
});

const copyFilters = (filters: LogFilters): LogFilters => ({
	...filters,
	logTypeCode: [...filters.logTypeCode],
	tagFilters: [...filters.tagFilters],
});

export const TableLogs = () => {
	const [draftFilters, setDraftFilters] = useState<LogFilters>(() =>
		getDefaultFilters(),
	);
	const [appliedFilters, setAppliedFilters] = useState<LogFilters>(() =>
		getDefaultFilters(),
	);
	const [isApplyingFilters, setIsApplyingFilters] = useState(false);
	const hasMountedRef = useRef(false);
	const [timezone, setTimezone] = useState<"UTC" | "Server">("UTC");
	const [search, setSearch] = useState<string>("");

	const [gridAPI, setGridAPI] = useState<GridApi | null>(null);
	const [error, setError] = useState<string>("");
	const [showLogModal, setShowLogModal] = useState<boolean>(false);
	const [selectedLog, setSelectedLog] = useState<Log | undefined>(undefined);

	const {
		data: rows,
		isLoading,
		isFetching,
		isError,
		error: e,
	} = useSearchLogs({
		startDate: appliedFilters.startDate,
		endDate: appliedFilters.endDate,
		logTypeCode: appliedFilters.logTypeCode.join(","),
		autoRefresh: appliedFilters.autoRefresh,
		tagFilters: appliedFilters.tagFilters,
	});

	const onGridReady = (params: GridReadyEvent) => {
		setGridAPI(params.api);
	};

	const formattedRows = useMemo(
		() =>
			rows?.map(
				({ LogId, Timestamp, Source, Message, LogTypeCode, User }: Log) => ({
					id: LogId,
					Timestamp,
					Source,
					Message: isNil(Message) ? "" : Message.replaceAll("Added", " Added"),
					LogTypeCode: getLogTypeByCodeId(LogTypeCode as string),
					User,
				}),
			),
		[rows],
	);

	const { columnDefs } = useLogTableData(timezone);
	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			suppressHeaderMenuButton: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		};
	}, []);

	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const handleAutoRefreshChange = (checked: boolean) => {
		const nextFilters: LogFilters = checked
			? {
					...draftFilters,
					startDate: moment().add(-15, "minutes").toDate(),
					endDate: null,
					autoRefresh: true,
				}
			: {
					...draftFilters,
					endDate: moment().hour(23).minute(59).second(59).toDate(),
					autoRefresh: false,
				};

		setDraftFilters(copyFilters(nextFilters));
	};

	const handleResetButtonClick = () => {
		const filters = getDefaultFilters();
		setDraftFilters(copyFilters(filters));
		setSearch("");
		setSelectedLog(undefined);
		setError("");
	};

	const rowClicked = (rowClickedEvent: RowDoubleClickedEvent<any>) => {
		let data = rowClickedEvent.data;
		if (!data) return;
		setSelectedLog(data as Log);
		setShowLogModal(true);
	};

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableLogs");
		return [];
	};

	const { getMenuOptions } = useLogsOptionsFunctions({
		setSelectedLog,
		setShowLogModal,
		handleResetButtonClick,
	});

	const filterContext = useMemo(
		() => ({
			startDate: appliedFilters.startDate,
			endDate: appliedFilters.endDate,
			logTypeCode: appliedFilters.logTypeCode.join(","),
			tagFilters: appliedFilters.tagFilters,
		}),
		[appliedFilters],
	);

	useEffect(() => {
		registerConfig({
			id: "TableLogs",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [isError, e]);

	useEffect(() => {
		if (!hasMountedRef.current) {
			hasMountedRef.current = true;
			return;
		}

		setIsApplyingFilters(true);
		const timeout = window.setTimeout(() => {
			setAppliedFilters(copyFilters(draftFilters));
			setIsApplyingFilters(false);
		}, 100);

		return () => window.clearTimeout(timeout);
	}, [draftFilters]);

	useEffect(() => {
		const timeout = window.setTimeout(() => {
			gridAPI?.setGridOption("quickFilterText", search);
		}, 350);

		return () => window.clearTimeout(timeout);
	}, [gridAPI, search]);

	return (
		<>
			<Grid2 container justifyContent="flex-start" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						MES and System Logs
					</Typography>
				</Grid2>
				<Grid2
					container
					justifyContent="flex-start"
					alignItems="center"
					sx={{
						width: "100%",

						"& .MuiDataGrid-row": {
							cursor: "pointer",
						},
						"& .MuiPaper-root ": {
							height: "100% !important",
						},
						"& .MuiDataGrid-columnHeaderTitle, & .MuiTablePagination-caption": {
							fontWeight: "bold",
						},
					}}
					spacing={2}
				>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Card
							sx={{
								backgroundColor: "#ffffff",
								display: "flex",
								alignItems: "center",
								height: "100%",
							}}
						>
							<CardContent sx={{ width: "100%" }}>
								<Grid2
									container
									alignItems="center"
									direction="row"
									spacing={2}
								>
									<Grid2 size={{ md: 1.5 }}>
										<DateFilter
											label="From"
											date={draftFilters.startDate}
											setDate={(date: Date | null) =>
												setDraftFilters({
													...draftFilters,
													startDate: date || new Date(),
												})
											}
											maxDate={draftFilters.endDate ?? undefined}
										/>
									</Grid2>
									<Grid2 size={{ md: 1.5 }}>
										<DateFilter
											label="To"
											date={draftFilters.endDate}
											setDate={(date: Date | null) => {
												if (date) {
													setDraftFilters({
														...draftFilters,
														endDate: date,
														autoRefresh: false,
													});
												}
											}}
											minDate={draftFilters.startDate}
										/>
									</Grid2>
									<Grid2 size={{ md: 2, xs: 12 }}>
										<CodeFilter
											LogTypeCode={draftFilters.logTypeCode}
											setLogTypeCodeFilter={(logTypeCodes: string[]) =>
												setDraftFilters({
													...draftFilters,
													logTypeCode: logTypeCodes,
												})
											}
										/>
									</Grid2>
									<Grid2 size={{ md: 1, xs: 12 }}>
										<TimezoneSelector value={timezone} onChange={setTimezone} />
									</Grid2>
									<Grid2 size={{ md: 1.5, xs: 12 }}>
										<FormControlLabel
											checked={draftFilters.autoRefresh}
											control={<Switch color="primary" />}
											label="Auto Refresh"
											onChange={(e, checked) =>
												handleAutoRefreshChange(checked)
											}
										/>
									</Grid2>
									<Grid2 size={{ md: 3.5, xs: 12 }}>
										<SearchFilter search={search} setSearch={setSearch} />
									</Grid2>
									<Grid2 size={{ md: 1, xs: 12 }} style={{ paddingTop: 8 }}>
										<Button
											variant="contained"
											color="inherit"
											onClick={handleResetButtonClick}
											fullWidth
										>
											Reset
										</Button>
									</Grid2>
									<Grid2 size={{ md: 12, xs: 12 }} sx={{ mt: 1 }}>
										<TagFilter
											tagFilters={draftFilters.tagFilters}
											setTagFilters={(tagFilters) =>
												setDraftFilters({ ...draftFilters, tagFilters })
											}
											filterContext={filterContext}
										/>
									</Grid2>
								</Grid2>
							</CardContent>
						</Card>
					</Grid2>
					<Grid2
						size={{ md: 12, xs: 12 }}
						style={{
							height: "70vh",
						}}
					>
						<Paper style={{ height: "100%", width: "100%" }}>
							<AgGridReact
								loading={isLoading || isApplyingFilters || isFetching}
								gridOptions={{
									theme: themeDXT,
									cacheQuickFilter: true,
								}}
								rowData={formattedRows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								onGridReady={onGridReady}
								getRowId={(params) => params.data.id}
								rowHeight={34}
								headerHeight={40}
								animateRows
								loadingOverlayComponent={CenteredLazyLoading}
								getContextMenuItems={(e: any) => getContextMenuItems(e)}
								rowSelection="single"
								onRowDoubleClicked={(e) => {
									rowClicked(e);
								}}
							/>
						</Paper>
					</Grid2>
				</Grid2>
				<ErrorModal error={error} onHide={() => setError("")} />
			</Grid2>
			<ModalLogSelected
				show={showLogModal}
				onHide={() => setShowLogModal(false)}
				selectedLog={selectedLog}
				suffixTitle="MES and System Logs"
			/>
		</>
	);
};
